import { AuthenticatedState, AuthenticatingState } from './models/auth-state';
import { AuthComponent } from './components/auth/auth.component';
import { AuthConfig } from './models/auth-config';
import { noop } from 'rxjs';
import { InjectionToken, Type } from '@angular/core';

const AUTH_CONFIG_KEY = 'AUTH_CONFIG';

export const defaultAuthConfig: AuthConfig = {
  settings: {
    enableExperimentalWorkflow: false,
    customAuthComponent: AuthComponent,
    enableDebugLog: false,
  },
  eventHandlers: {
    onAuthenticated: noop,
    onAuthenticating: noop,
    onLoggedOut: noop,
    onLoggingOut: noop,
    onSwitchingAccount: noop,
  },
  textMap: {
    appName: '',
    appDescription: '',
    appTagline: '',
    loginFieldLabel: 'Username',
    loginFieldPlaceholder: 'Email Address',
    continueButtonText: 'Continue',
    continueButtonLoadingText: 'Continuing...',
    accountSelectorLabel: 'Select an Account',
    redirectText: 'Logging In...',
  },
  errorMap: {
    accountExpired:
      'Failed to sign in. Account expired. Try choosing a different account.',
    genericError: 'Something went wrong, please try again.',
    loginNotExist: 'User login does not exist.',
    tidTokenExpired:
      'The Trimble Identity Token has expired. Please try again.',
    unauthorized:
      'You do not have access to this account. Please contact your administrator.',
    notLicensedForProduct:
      "Failed to login. The user doesn't belong to an account licensed for this product. Contact your account admin for access.",
    usernameNotExist: 'Username does not exist.',
  },
  trimbleIdentity: {
    trimbleCloudAppName: '',
    trimbleCloudClientId: '',
    trimbleMapsAppName: '',
    callbackUrl: `${location.origin}/auth/redirect`,
  },
};

export const AUTH_CONFIG = new InjectionToken<AuthConfig>(AUTH_CONFIG_KEY, {
  providedIn: 'root',
  factory: () => defaultAuthConfig,
});

type DeepPartial<T> = T extends unknown[]
  ? T
  : T extends Type<unknown>
    ? T
    : T extends () => void
      ? T
      : T extends (data: Omit<AuthenticatedState, 'state'>) => void
        ? T
        : T extends (data: Omit<AuthenticatingState, 'state'>) => void
          ? T
          : T extends object
            ? { [P in keyof T]?: DeepPartial<T[P]> }
            : T;

export type PartialAuthConfig = DeepPartial<AuthConfig>;

export function authConfig(config: PartialAuthConfig = {}): AuthConfig {
  return {
    ...defaultAuthConfig,
    ...config,
    settings: {
      ...defaultAuthConfig.settings,
      ...config.settings,
    },
    eventHandlers: {
      ...defaultAuthConfig.eventHandlers,
      ...config.eventHandlers,
    },
    textMap: {
      ...defaultAuthConfig.textMap,
      ...config.textMap,
    },
    errorMap: {
      ...defaultAuthConfig.errorMap,
      ...config.errorMap,
    },
    trimbleIdentity: {
      ...defaultAuthConfig.trimbleIdentity,
      ...config.trimbleIdentity,
    },
  };
}
